/**
 * Local-development feature flag overrides — entirely disabled in production.
 *
 * Override resolution order (highest wins):
 *   1. `localStorage.__dev_feature_flags__` — JSON map, togglable at runtime without restart
 *   2. `VITE_DEV_FEATURE_FLAGS` env var — comma-separated `key:true/false`, requires dev-server restart
 *   3. API response — unchanged
 *
 * Usage:
 *   # .env.local (gitignored — affects only your machine)
 *   VITE_DEV_FEATURE_FLAGS=trial-flow:true,experiments:false
 *
 *   # Browser console (runtime, no restart needed)
 *   localStorage.setItem('__dev_feature_flags__', JSON.stringify({ 'trial-flow': true }))
 *   # Then refresh the page.
 *
 * Note: `localStorage` is accessed directly here by design — this is a developer
 * tooling utility, not user-facing persisted state, so Zustand persistence is
 * intentionally bypassed.
 */

const LOCAL_STORAGE_KEY = '__dev_feature_flags__';

/** Parses a `key:true,other:false` string from the env var into a flag map. */
function parseEnvOverrides(raw: string): Record<string, boolean> {
  if (!raw) return {};
  return Object.fromEntries(
    raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .flatMap((pair) => {
        const colonIdx = pair.indexOf(':');
        if (colonIdx === -1) return [];
        const key = pair.slice(0, colonIdx).trim();
        const value = pair.slice(colonIdx + 1).trim();
        if (!key) return [];
        return [[key, value === 'true']];
      }),
  );
}

/** Reads the JSON override map from localStorage, silently returns {} on any failure. */
function readLocalStorageOverrides(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(([, v]) => typeof v === 'boolean'),
    ) as Record<string, boolean>;
  } catch {
    return {};
  }
}

/**
 * Returns the merged local override map (localStorage > env var) in dev mode.
 * Always returns `{}` in production so callers can unconditionally spread the result.
 *
 * @param envOverrideStr - Value of `VITE_DEV_FEATURE_FLAGS` from the typed ENV object.
 */
export function getDevFlagOverrides(envOverrideStr: string): Record<string, boolean> {
  if (!import.meta.env.DEV) return {};
  return { ...parseEnvOverrides(envOverrideStr), ...readLocalStorageOverrides() };
}
